import React, { useEffect, useMemo } from 'react';
import { DonutGraph } from './DonutGraph';
import { NonIdealState } from '@blueprintjs/core';
import { selectDilDefinitionByName } from 'srs-middleware';
import { DefaultChartColorScales } from 'styles/chartColors';
import { NO_DATA_MESSAGE } from 'widgets/chartUtils';
import { useWidgetControlsContext } from 'views/common/WidgetControls';

/**
 * ChartWrapper for the Donut Graph.
 *
 * @param dimensions
 * @param data
 * @param selectedDefinitionName
 * @param endpointQuery
 * @param widgetConfiguration
 * @param getColorScale
 * @returns {*}
 * @constructor
 */
export function DonutGraphWrapper({
	getColorScale,
	dimensions,
	data,
	noDataMessage=NO_DATA_MESSAGE,
	selectedDefinitionName,
	endpointQuery,
	widgetConfiguration,
}) {
	// get the actual definition
	const selectedDefinition = useMemo(() => {
		return selectDilDefinitionByName(selectedDefinitionName);
	}, [selectedDefinitionName]);
	const {dispatch, state} = useWidgetControlsContext();
	const hideLabels = state?.hideLabels;
	const convertedData = useMemo(() => {
		if (!data?.entries || !selectedDefinition || !endpointQuery?.options?.propertyList)
			return;

		const items = data.entries || [];

		// empty data is "valid"
		if (items.length === 0)
			return [];

		const converted = items.map(({ key, count, value }) => {
			return {
				name: key,
				value: value,
				reference: count,
			};
		});

		if (widgetConfiguration?.colorScale === DefaultChartColorScales.Sequential) {
			getColorScale.domain([0, converted.length || 0]);
			converted.forEach((d, index) => {
				d.colorValue = index;
			});
		} else {
			getColorScale.domain(converted.map((d) => d.name));
			converted.forEach((d) => {
				d.colorValue = d.name;
			});
		}

		return converted;
	}, [data.entries, selectedDefinition, endpointQuery, widgetConfiguration, getColorScale]);

	//has to be in a 'useEffect' as opposed to the useMemo above bc of the way react is.
	useEffect(() => {
		// the data is null or in the wrong shape and we exit
		if (!data?.entries || !selectedDefinition || !endpointQuery?.options?.propertyList || data?.entries?.length === 0) {
			dispatch && dispatch({ type: "setLabelsToggleBtnStatus", payload: false });
			return;
		}
		dispatch && dispatch({ type: "setLabelsToggleBtnStatus", payload: true });

	}, [data]);

	const _widgetConfig = useMemo(() => {
		return {...widgetConfiguration, hideLabels};
	}, [widgetConfiguration, hideLabels]);

	// If the state is not ideal (the data is null or empty) render the nonIdealState message
	return convertedData?.length > 0 && getColorScale ? (
		<DonutGraph
			data={convertedData}
			containerDimensions={dimensions}
			widgetConfiguration={_widgetConfig}
			getColor={getColorScale}
		/>
	) : (
		<NonIdealState
			title="No Data Found"
			icon="graph-remove"
			description={noDataMessage}
		/>
	);
}
